/* eslint-disable typescript-extended-rules/file-naming-convention */
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { QuillModule } from 'ngx-quill';

import { StateLabelPipe } from '../../core/pipes/state-label.pipe';
import { SharedModule } from '../../shared/shared.module';

import { OrganizationsLegacyService } from './organizations-legacy.service';
import { OrganizationsRoutingModule } from './organizations-routing.module';
import { OrganizationsEffects } from './state/organizations.effects';
import { OrganizationsReducer } from './state/organizations.reducer';
import { ORGANIZATIONS_STATE_NAME } from './state/organizations.selector';
import { BatchActionInfoComponent } from './views/batch-action-info/batch-action-info.component';
import { BatchActionsComponent } from './views/batch-actions/batch-actions.component';
import {
	BeneficiaryCreationComponent,
	CpfAlertDialogComponent,
} from './views/beneficiary-creation/beneficiary-creation.component';
import { BeneficiaryEditComponent } from './views/beneficiary-edit/beneficiary-edit.component';
import { BeneficiaryReconciliationComponent } from './views/beneficiary-reconciliation/beneficiary-reconciliation.component';
import { BeneficiaryReconciliationApplyPreviewModalComponent } from './views/beneficiary-reconciliation/beneficiary-reconciliation-apply-preview-modal/beneficiary-reconciliation-apply-preview-modal.component';
import { BeneficiaryReconciliationHistoryDetailModalComponent } from './views/beneficiary-reconciliation/beneficiary-reconciliation-history-detail-modal/beneficiary-reconciliation-history-detail-modal.component';
import { BeneficiaryReconciliationInlineResolutionModalComponent } from './views/beneficiary-reconciliation/beneficiary-reconciliation-inline-resolution-modal/beneficiary-reconciliation-inline-resolution-modal.component';
import { BeneficiaryReconciliationMappingModalComponent } from './views/beneficiary-reconciliation/beneficiary-reconciliation-mapping-modal/beneficiary-reconciliation-mapping-modal.component';
import { BeneficiaryReconciliationPreflightOrganizationModalComponent } from './views/beneficiary-reconciliation/beneficiary-reconciliation-preflight-organization-modal/beneficiary-reconciliation-preflight-organization-modal.component';
import { BeneficiaryReconciliationPreflightRowModalComponent } from './views/beneficiary-reconciliation/beneficiary-reconciliation-preflight-row-modal/beneficiary-reconciliation-preflight-row-modal.component';
import { BeneficiaryReconciliationTitularityResolutionModalComponent } from './views/beneficiary-reconciliation/beneficiary-reconciliation-titularity-resolution-modal/beneficiary-reconciliation-titularity-resolution-modal.component';
import { BeneficiaryReconciliationValueResolutionModalComponent } from './views/beneficiary-reconciliation/beneficiary-reconciliation-value-resolution-modal/beneficiary-reconciliation-value-resolution-modal.component';
import { BeneficiaryRegistrationComponent } from './views/beneficiary-registration/beneficiary-registration.component';
import { ManageDependentModalComponent } from './views/beneficiary-registration/manage-dependent-modal/manage-dependent-modal.component';
import { AddCongenerBenefitModalComponent } from './views/beneficiary-view/add-congener-benefit-modal/add-congener-benefit-modal.component';
import {
	BeneficiaryAttachDialogComponent,
	BeneficiaryViewComponent,
} from './views/beneficiary-view/beneficiary-view.component';
import { CreateEditDependentModalComponent } from './views/beneficiary-view/create-edit-dependent-modal/create-edit-dependent-modal.component';
import { DeleteDependentModalComponent } from './views/beneficiary-view/delete-dependent-modal/delete-dependent-modal.component';
import { DependentInfoComponent } from './views/beneficiary-view/dependent-info/dependent-info.component';
import { EditBenefitsModalComponent } from './views/beneficiary-view/edit-benefits-modal/edit-benefits-modal.component';
import { EditPoliciesModalComponent } from './views/beneficiary-view/edit-policies-modal/edit-policies-modal.component';
import { ManageBenefitDateModalComponent } from './views/beneficiary-view/manage-benefit-date-modal/manage-benefit-date-modal.component';
import { CancelTransferModalComponent } from './views/beneficiary-view/modals/cancel-transfer-modal/cancel-transfer-modal.component';
import { TransferBeneficiaryModalComponent } from './views/beneficiary-view/modals/transfer-beneficiary-modal/transfer-beneficiary-modal.component';
import { BenefitRegistrationComponent } from './views/benefit-registration/benefit-registration.component';
import {
	BenefitsComponent,
	DelBenefitDialogComponent,
} from './views/benefits/benefits.component';
import { ClaimUsageIngestionComponent } from './views/claim-usage-ingestion/claim-usage-ingestion.component';
import { ClaimUsageIngestionProcessingDetailDialogComponent } from './views/claim-usage-ingestion/claim-usage-ingestion-processing-detail-dialog.component';
import { ClaimUsageIngestionUploadDialogComponent } from './views/claim-usage-ingestion/claim-usage-ingestion-upload-dialog.component';
import { ContractAttachmentDropzoneComponent } from './views/contracts/contract-attachment-dropzone.component';
import { ContractCodeEditorComponent } from './views/contracts/contract-code-editor.component';
import { ContractHistoryDialogComponent } from './views/contracts/contract-history-dialog.component';
import { ContractRichContentComponent } from './views/contracts/contract-rich-content.component';
import { ContractSectionEditorComponent } from './views/contracts/contract-section-editor.component';
import { ContractsComponent } from './views/contracts/contracts.component';
import { DigitalHospitalComponent } from './views/digital-hospital/digital-hospital.component';
import { OrgHubComponent } from './views/org-hub/org-hub.component';
import { OrgRegistrationComponent } from './views/org-registration/org-registration.component';
import { ProductsModalComponent } from './views/org-registration/products-modal/products-modal.component';
import { OrgReportsComponent } from './views/org-reports/org-reports.component';
import { OrgReportsLinkComponent } from './views/org-reports-link/org-reports-link.component';
import { DeactivateOrganizationModalComponent } from './views/organizations/deactivate-organization-modal/deactivate-organization-modal.component';
import { OrganizationInfoComponent } from './views/organizations/organization-info/organization-info.component';
import { OrganizationsComponent } from './views/organizations/organizations.component';
import { LinkOrganizationsModalComponent } from './views/partner-brokers/link-organizations-modal/link-organizations-modal.component';
import { PartnerBrokerRegistrationComponent } from './views/partner-brokers/partner-broker-registration/partner-broker-registration.component';
import { PartnerBrokersComponent } from './views/partner-brokers/partner-brokers.component';
import { PartnerRegistrationComponent } from './views/partner-registration/partner-registration.component';
import { PartnersComponent } from './views/partners/partners.component';
import {
	DelPolicyDialogComponent,
	PoliciesComponent,
} from './views/policies/policies.component';
import {
	PolicyAlertDialogComponent,
	PolicyRegistrationComponent,
} from './views/policy-registration/policy-registration.component';
import { PositionModalComponent } from './views/policy-registration/position-modal/position-modal.component';
import { BenefitModalComponent } from './views/position-registration/benefit-modal/benefit-modal.component';
import {
	PositionAlertDialogComponent,
	PositionRegistrationComponent,
} from './views/position-registration/position-registration.component';
import {
	DelPositionDialogComponent,
	PositionsComponent,
} from './views/positions/positions.component';
import { ScoreComponent } from './views/score/score.component';
import { SecurityComponent } from './views/security/security.component';
import { OrgGroupModalComponent } from './views/user-registration/org-group-modal/org-group-modal.component';
import { OrgListModalComponent } from './views/user-registration/org-list-modal/org-list-modal.component';
import {
	ResetPassAlertDialogComponent,
	UserRegistrationComponent,
} from './views/user-registration/user-registration.component';
import { UsersComponent } from './views/users/users.component';
import { WhitelabelComponent } from './views/whitelabel/whitelabel.component';

@NgModule({
	declarations: [
		BatchActionInfoComponent,
		BatchActionsComponent,
		BeneficiaryAttachDialogComponent,
		BeneficiaryCreationComponent,
		BeneficiaryEditComponent,
		BeneficiaryReconciliationApplyPreviewModalComponent,
		BeneficiaryReconciliationComponent,
		BeneficiaryReconciliationHistoryDetailModalComponent,
		BeneficiaryReconciliationInlineResolutionModalComponent,
		BeneficiaryReconciliationMappingModalComponent,
		BeneficiaryReconciliationPreflightOrganizationModalComponent,
		BeneficiaryReconciliationPreflightRowModalComponent,
		BeneficiaryReconciliationTitularityResolutionModalComponent,
		BeneficiaryReconciliationValueResolutionModalComponent,
		BeneficiaryRegistrationComponent,
		BeneficiaryViewComponent,
		BenefitModalComponent,
		BenefitRegistrationComponent,
		BenefitsComponent,
		CpfAlertDialogComponent,
		CreateEditDependentModalComponent,
		DelBenefitDialogComponent,
		DeleteDependentModalComponent,
		DelPolicyDialogComponent,
		DelPositionDialogComponent,
		DependentInfoComponent,
		EditBenefitsModalComponent,
		AddCongenerBenefitModalComponent,
		EditPoliciesModalComponent,
		ManageBenefitDateModalComponent,
		TransferBeneficiaryModalComponent,
		CancelTransferModalComponent,
		ClaimUsageIngestionComponent,
		ClaimUsageIngestionProcessingDetailDialogComponent,
		ClaimUsageIngestionUploadDialogComponent,
		ContractAttachmentDropzoneComponent,
		ContractHistoryDialogComponent,
		ContractsComponent,
		ContractCodeEditorComponent,
		ContractSectionEditorComponent,
		ContractRichContentComponent,
		ManageDependentModalComponent,
		OrganizationInfoComponent,
		OrganizationsComponent,
		DeactivateOrganizationModalComponent,
		PartnerBrokersComponent,
		PartnerBrokerRegistrationComponent,
		OrgGroupModalComponent,
		OrgHubComponent,
		OrgListModalComponent,
		OrgRegistrationComponent,
		OrgReportsComponent,
		OrgReportsLinkComponent,
		PartnerRegistrationComponent,
		PartnersComponent,
		PoliciesComponent,
		PolicyAlertDialogComponent,
		PolicyRegistrationComponent,
		PositionAlertDialogComponent,
		PositionModalComponent,
		PositionRegistrationComponent,
		PositionsComponent,
		ProductsModalComponent,
		ResetPassAlertDialogComponent,
		ScoreComponent,
		StateLabelPipe,
		UserRegistrationComponent,
		UsersComponent,
		WhitelabelComponent,
		SecurityComponent,
		LinkOrganizationsModalComponent,
		DigitalHospitalComponent,
	],
	exports: [CancelTransferModalComponent],
	imports: [
		CommonModule,
		MatTableModule,
		MatTooltipModule,
		MatAutocompleteModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		MatButtonModule,
		MatChipsModule,
		MatProgressSpinnerModule,
		MatCheckboxModule,
		MatDialogModule,
		MatSelectModule,
		MatDatepickerModule,
		MatNativeDateModule,
		OrganizationsRoutingModule,
		ReactiveFormsModule,
		QuillModule.forRoot(),
		SharedModule,
		MatProgressSpinnerModule,
		EffectsModule.forFeature([OrganizationsEffects]),
		StoreModule.forFeature(ORGANIZATIONS_STATE_NAME, OrganizationsReducer),
	],
	providers: [OrganizationsLegacyService],
})
export class OrganizationsModule {}
